import React, { useRef, useState, useEffect } from 'react';
import {
    FaChevronLeft,
    FaChevronRight,
    FaNewspaper,
    FaLaptop,
    FaTree,
    FaHeartbeat,
    FaRocket,
    FaDollarSign,
    FaGraduationCap,
    FaRunning,
    FaFilm,
    FaPaintBrush,
    FaUtensils,
    FaBriefcase,
    FaBolt,
    FaPlane,
    FaHome,
    FaAppleAlt,
    FaFlask,
    FaTshirt,
    FaBaby,
    FaHashtag,
    FaLightbulb,
    FaShoppingCart,
    FaGamepad,
    FaBalanceScale,
    FaFutbol,
    FaTableTennis,
    FaBaseballBall,
    FaFootballBall,
    FaStar,
    FaSearch,
    FaFilter
} from 'react-icons/fa';

const topics = [
    { id: 1, name: 'News', icon: <FaNewspaper /> },
    { id: 2, name: 'Technology', icon: <FaLaptop /> },
    { id: 3, name: 'Environment', icon: <FaTree /> },
    { id: 4, name: 'Health', icon: <FaHeartbeat /> },
    { id: 5, name: 'Space', icon: <FaRocket /> },
    { id: 6, name: 'Finance', icon: <FaDollarSign /> },
    { id: 7, name: 'Education', icon: <FaGraduationCap /> },
    { id: 8, name: 'Sports', icon: <FaRunning /> },
    { id: 9, name: 'Entertainment', icon: <FaFilm /> },
    { id: 10, name: 'Culture', icon: <FaPaintBrush /> },
    { id: 11, name: 'Cooking', icon: <FaUtensils /> },
    { id: 12, name: 'Business', icon: <FaBriefcase /> },
    { id: 13, name: 'Energy', icon: <FaBolt /> },
    { id: 14, name: 'Travel', icon: <FaPlane /> },
    { id: 15, name: 'Home', icon: <FaHome /> },
    { id: 16, name: 'Food', icon: <FaAppleAlt /> },
    { id: 17, name: 'Science', icon: <FaFlask /> },
    { id: 18, name: 'Fashion', icon: <FaTshirt /> },
    { id: 19, name: 'Parenting', icon: <FaBaby /> },
    { id: 20, name: 'Social Media', icon: <FaHashtag /> },
    { id: 21, name: 'Innovation', icon: <FaLightbulb /> },
    { id: 22, name: 'Shopping', icon: <FaShoppingCart /> },
    { id: 23, name: 'Gaming', icon: <FaGamepad /> },
    { id: 24, name: 'Politics', icon: <FaBalanceScale /> },
    { id: 25, name: 'Soccer', icon: <FaFutbol /> },
    { id: 26, name: 'Tennis', icon: <FaTableTennis /> },
    { id: 27, name: 'Baseball', icon: <FaBaseballBall /> },
    { id: 28, name: 'Football', icon: <FaFootballBall /> },
];

const TopicFilter = ({ onSelect, firstTopics = [], width = '100%', showSearch = false, onSearchToggle = null, onTopicsReady = null }) => {
    const containerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const orderedTopics = React.useMemo(() => {
        const prioritizedTopics = firstTopics
            .map((topicName) => topics.find((topic) => topic.name === topicName))
            .filter(Boolean);
        const remainingTopics = topics.filter(
            (topic) => !prioritizedTopics.includes(topic)
        );
        return [...prioritizedTopics, ...remainingTopics];
    }, [firstTopics]);

    // Notify parent that topics are ready
    useEffect(() => {
        if (onTopicsReady && orderedTopics.length > 0) {
            onTopicsReady(orderedTopics);
        }
    }, [orderedTopics, onTopicsReady]);

    const updateScrollState = () => {
        if (containerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
        }
    };

    useEffect(() => {
        updateScrollState();
        const ref = containerRef.current;
        if (ref) {
            ref.addEventListener('scroll', updateScrollState);
        }
        return () => {
            if (ref) {
                ref.removeEventListener('scroll', updateScrollState);
            }
        };
    }, []);

    const scroll = (direction) => {
        if (containerRef.current) {
            const { scrollLeft, clientWidth } = containerRef.current;
            // Calculate item width (80px icon + 20px gap + margins)
            const itemWidth = 120; // Approximate width of each topic item
            const itemsPerView = Math.floor(clientWidth / itemWidth);
            const scrollAmount = itemWidth * Math.max(1, itemsPerView - 1); // Scroll by visible items minus one for overlap
            
            const newScrollLeft = direction === 'left' 
                ? Math.max(0, scrollLeft - scrollAmount)
                : scrollLeft + scrollAmount;
                
            containerRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth',
            });
        }
    };

    const toggleTopicSelection = (topicName) => {
        setSelectedTopics((prevSelected) => {
            const updatedSelection = prevSelected.includes(topicName)
                ? prevSelected.filter((name) => name !== topicName)
                : [...prevSelected, topicName];

            if (onSelect) {
                onSelect(updatedSelection);
            }

            return updatedSelection;
        });
    };

    // On mobile, just return empty div - modal will be handled by parent
    if (isMobile) {
        return <div style={{ display: 'none' }}></div>;
    }

    // Desktop View
    return (
        <>
            <style>
                {`
                .topic-scroll-container::-webkit-scrollbar {
                    display: none;
                }
                
                .scroll-fade-overlay {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 80px;
                    pointer-events: none;
                    z-index: 3;
                }
                
                .scroll-fade-left {
                    left: 0;
                    background: linear-gradient(to right, 
                        rgba(255,255,255,1) 0%, 
                        rgba(255,255,255,0.95) 20%, 
                        rgba(255,255,255,0.8) 40%, 
                        rgba(255,255,255,0.4) 70%, 
                        rgba(255,255,255,0) 100%
                    );
                }
                
                .scroll-fade-right {
                    right: 0;
                    background: linear-gradient(to left, 
                        rgba(255,255,255,1) 0%, 
                        rgba(255,255,255,0.95) 20%, 
                        rgba(255,255,255,0.8) 40%, 
                        rgba(255,255,255,0.4) 70%, 
                        rgba(255,255,255,0) 100%
                    );
                }
                
                .scroll-button {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    box-shadow: 
                        0 4px 6px -1px rgba(0, 0, 0, 0.1),
                        0 2px 4px -1px rgba(0, 0, 0, 0.06),
                        inset 0 1px 0 rgba(255, 255, 255, 0.1);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    flex-shrink: 0;
                    position: relative;
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                }
                
                @media (max-width: 767px) {
                    .scroll-button {
                        width: 32px;
                        height: 32px;
                        border-radius: 8px;
                    }
                }
                
                @media (min-width: 768px) {
                    .scroll-button {
                        width: 48px;
                        height: 48px;
                        border-radius: 12px;
                    }
                }
                
                .scroll-button::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: 12px;
                    padding: 1px;
                    background: linear-gradient(145deg, rgba(0,202,153,0.1), rgba(124,77,255,0.1));
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: subtract;
                    mask-composite: exclude;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }
                
                .scroll-button:hover {
                    background: linear-gradient(145deg, #ffffff 0%, #f1f5f9 100%);
                    box-shadow: 
                        0 10px 15px -3px rgba(0, 0, 0, 0.1),
                        0 4px 6px -2px rgba(0, 0, 0, 0.05),
                        inset 0 1px 0 rgba(255, 255, 255, 0.2),
                        0 0 0 3px rgba(0,202,153,0.1);
                    transform: translateY(-2px);
                }
                
                .scroll-button:hover::before {
                    opacity: 1;
                }
                
                .scroll-button:active {
                    transform: translateY(0px);
                    box-shadow: 
                        0 4px 6px -1px rgba(0, 0, 0, 0.1),
                        0 2px 4px -1px rgba(0, 0, 0, 0.06),
                        inset 0 2px 4px rgba(0, 0, 0, 0.1);
                }
                
                .scroll-button-icon {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
                }
                
                .scroll-button:hover .scroll-button-icon {
                    color: #00ca99 !important;
                    transform: scale(1.1);
                }
                
                .scroll-mask {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 80px;
                    pointer-events: none;
                    z-index: 4;
                }
                
                .scroll-mask-left {
                    left: 0;
                    background: linear-gradient(to right, #ffffff 0%, transparent 100%);
                }
                
                .scroll-mask-right {
                    right: 0;
                    background: linear-gradient(to left, #ffffff 0%, transparent 100%);
                }
                `}
            </style>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: width,
                    gap: isMobile ? '6px' : '15px',
                }}
            >
                {/* Left scroll button */}
                {canScrollLeft ? (
                    <div 
                        className="scroll-button"
                        onClick={() => scroll('left')}
                    >
                        <FaChevronLeft
                            className="scroll-button-icon"
                            style={{
                                fontSize: isMobile ? '12px' : '20px',
                                color: '#64748b',
                            }}
                        />
                    </div>
                ) : (
                    <div style={{ width: isMobile ? '32px' : '48px', flexShrink: 0 }} /> // Placeholder to maintain layout
                )}
                
                {/* Carousel Container */}
                <div
                    style={{
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        flexGrow: 1,
                        minWidth: 0,
                    }}
                >
                    {/* Left fade overlay */}
                    {canScrollLeft && <div className="scroll-fade-overlay scroll-fade-left" />}
                    
                    {/* Right fade overlay */}
                    {canScrollRight && <div className="scroll-fade-overlay scroll-fade-right" />}
                    
                    {/* Additional mask layers for stronger fade effect */}
                    {canScrollLeft && <div className="scroll-mask scroll-mask-left" />}
                    {canScrollRight && <div className="scroll-mask scroll-mask-right" />}
                    
                    <div
                    ref={containerRef}
                    style={{
                        display: 'flex',
                        gap: isMobile ? '6px' : '12px',
                        padding: isMobile ? '4px 6px' : '8px 16px',
                        overflow: 'hidden',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        WebkitOverflowScrolling: 'touch',
                        flexGrow: 1,
                        width: 0, // Forces the container to not expand based on content
                        minWidth: '0',
                        scrollSnapType: 'x proximity', // Smooth scroll alignment
                    }}
                    className="topic-scroll-container"
                >
                    <div
                        key={'You may love..'}
                        onClick={() => toggleTopicSelection('You may love..')}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            flexShrink: 0,
                            margin: '0 3px',
                            scrollSnapAlign: 'center', // Snap alignment
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)';
                            e.currentTarget.querySelector('.icon-circle').style.boxShadow = selectedTopics.includes('You may love..') 
                                ? '0px 8px 25px rgba(0,202,153,0.4), 0px 4px 15px rgba(255,193,7,0.3)'
                                : '0px 6px 20px rgba(0,0,0,0.15)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'scale(1) translateY(0)';
                            e.currentTarget.querySelector('.icon-circle').style.boxShadow = selectedTopics.includes('You may love..') 
                                ? '0px 4px 15px rgba(0,202,153,0.3)'
                                : '0px 2px 8px rgba(0,0,0,0.1)';
                        }}
                    >
                        <div 
                            className="icon-circle"
                                style={{ 
                                    width: isMobile ? '40px' : '60px',
                                    height: isMobile ? '40px' : '60px',
                                    borderRadius: '50%',
                                    border: selectedTopics.includes('You may love..') ? '2px solid transparent' : '1px solid #e0e0e0',
                                background: selectedTopics.includes('You may love..') 
                                    ? 'linear-gradient(135deg, #00ca99 0%, #00b8d4 50%, #7c4dff 100%)'
                                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                boxShadow: selectedTopics.includes('You may love..') 
                                    ? '0px 3px 10px rgba(0,202,153,0.3)'
                                    : '0px 1px 4px rgba(0,0,0,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: isMobile ? '4px' : '6px',
                                position: 'relative',
                            }}
                        >
                            {selectedTopics.includes('You may love..') && (
                                <div style={{
                                    position: 'absolute',
                                    inset: '-3px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #00ca99, #00b8d4, #7c4dff, #ff6b6b)',
                                    zIndex: -1,
                                }} />
                            )}
                            <div style={{ 
                                fontSize: isMobile ? '14px' : '20px', 
                                color: selectedTopics.includes('You may love..') ? '#ffffff' : '#00ca99',
                                filter: selectedTopics.includes('You may love..') ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' : 'none'
                            }}>
                                <FaStar />
                            </div>
                        </div>
                        <span style={{ 
                            fontWeight: selectedTopics.includes('You may love..') ? 'bold' : '600',
                            fontSize: isMobile ? '8px' : '10px',
                            color: selectedTopics.includes('You may love..') ? '#00ca99' : '#555',
                            maxWidth: isMobile ? '45px' : '70px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textShadow: selectedTopics.includes('You may love..') ? '0 1px 2px rgba(0,202,153,0.2)' : 'none'
                        }}>You may love..</span>
                    </div>
                    
                    {/* Search Circle */}
                    {onSearchToggle && (
                        <div
                            onClick={() => onSearchToggle(!showSearch)}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                flexShrink: 0,
                                margin: '0 3px',
                                scrollSnapAlign: 'center',
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)';
                                e.currentTarget.querySelector('.search-circle').style.boxShadow = showSearch 
                                    ? '0px 8px 25px rgba(99,102,241,0.4), 0px 4px 15px rgba(139,92,246,0.3)'
                                    : '0px 6px 20px rgba(0,0,0,0.15)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'scale(1) translateY(0)';
                                e.currentTarget.querySelector('.search-circle').style.boxShadow = showSearch 
                                    ? '0px 4px 15px rgba(99,102,241,0.3)'
                                    : '0px 2px 8px rgba(0,0,0,0.1)';
                            }}
                        >
                            <div 
                                className="search-circle"
                                style={{ 
                                    width: isMobile ? '40px' : '60px',
                                    height: isMobile ? '40px' : '60px',
                                    borderRadius: '50%',
                                    border: showSearch ? '2px solid transparent' : '1px solid #e0e0e0',
                                    background: showSearch 
                                        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)'
                                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                    boxShadow: showSearch 
                                        ? '0px 3px 10px rgba(99,102,241,0.3)'
                                        : '0px 1px 4px rgba(0,0,0,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: isMobile ? '4px' : '6px',
                                    position: 'relative',
                                }}
                            >
                                {showSearch && (
                                    <div style={{
                                        position: 'absolute',
                                        inset: '-3px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)',
                                        zIndex: -1,
                                    }} />
                                )}
                                <FaSearch style={{ 
                                    fontSize: isMobile ? '14px' : '20px', 
                                    color: showSearch ? '#ffffff' : '#6366f1',
                                    filter: showSearch ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' : 'none'
                                }} />
                            </div>
                            <span style={{ 
                                fontWeight: showSearch ? 'bold' : '600',
                                fontSize: isMobile ? '8px' : '10px',
                                color: showSearch ? '#6366f1' : '#555',
                                maxWidth: isMobile ? '45px' : '70px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textShadow: showSearch ? '0 1px 2px rgba(99,102,241,0.2)' : 'none'
                            }}>Search</span>
                        </div>
                    )}
                    
                    {orderedTopics.map((topic) => (
                        <div
                            key={topic.id}
                            onClick={() => toggleTopicSelection(topic.name)}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                flexShrink: 0,
                                margin: '0 3px',
                                scrollSnapAlign: 'center', // Snap alignment
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)';
                                e.currentTarget.querySelector('.icon-circle').style.boxShadow = selectedTopics.includes(topic.name)
                                    ? '0px 8px 25px rgba(0,202,153,0.4), 0px 4px 15px rgba(255,193,7,0.3)'
                                    : '0px 6px 20px rgba(0,0,0,0.15)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'scale(1) translateY(0)';
                                e.currentTarget.querySelector('.icon-circle').style.boxShadow = selectedTopics.includes(topic.name)
                                    ? '0px 4px 15px rgba(0,202,153,0.3)'
                                    : '0px 2px 8px rgba(0,0,0,0.1)';
                            }}
                        >
                            <div 
                                className="icon-circle"
                                style={{ 
                                    width: isMobile ? '40px' : '60px',
                                    height: isMobile ? '40px' : '60px',
                                    borderRadius: '50%',
                                    border: selectedTopics.includes(topic.name) ? '2px solid transparent' : '1px solid #e0e0e0',
                                    background: selectedTopics.includes(topic.name) 
                                        ? 'linear-gradient(135deg, #00ca99 0%, #00b8d4 50%, #7c4dff 100%)'
                                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                    boxShadow: selectedTopics.includes(topic.name)
                                        ? '0px 3px 10px rgba(0,202,153,0.3)'
                                        : '0px 1px 4px rgba(0,0,0,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: isMobile ? '4px' : '6px',
                                    position: 'relative',
                                }}
                            >
                                {selectedTopics.includes(topic.name) && (
                                    <div style={{
                                        position: 'absolute',
                                        inset: '-3px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #00ca99, #00b8d4, #7c4dff, #ff6b6b)',
                                        zIndex: -1,
                                    }} />
                                )}
                                <div style={{ 
                                    fontSize: isMobile ? '14px' : '20px', 
                                    color: selectedTopics.includes(topic.name) ? '#ffffff' : '#00ca99',
                                    filter: selectedTopics.includes(topic.name) ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' : 'none'
                                }}>
                                    {topic.icon}
                                </div>
                            </div>
                            <span style={{ 
                                fontWeight: selectedTopics.includes(topic.name) ? 'bold' : '600',
                                fontSize: isMobile ? '8px' : '10px',
                                color: selectedTopics.includes(topic.name) ? '#00ca99' : '#555',
                                maxWidth: isMobile ? '45px' : '70px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textShadow: selectedTopics.includes(topic.name) ? '0 1px 2px rgba(0,202,153,0.2)' : 'none'
                            }}>{topic.name}</span>
                        </div>
                    ))}
                    </div>
                </div>
                
                {/* Right scroll button */}
                {canScrollRight ? (
                    <div 
                        className="scroll-button"
                        onClick={() => scroll('right')}
                    >
                        <FaChevronRight
                            className="scroll-button-icon"
                            style={{
                                fontSize: isMobile ? '12px' : '20px',
                                color: '#64748b',
                            }}
                        />
                    </div>
                ) : (
                    <div style={{ width: isMobile ? '32px' : '48px', flexShrink: 0 }} /> // Placeholder to maintain layout
                )}
            </div>
        </>
    );
};

export default TopicFilter;
